import { useEffect, useRef } from 'react';
import {
    Card,
    CardBody,
    CardHeader,
    Input,
    Button,
    Avatar,
    Divider,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useDisclosure,
    cn,
    Tooltip,
} from '@heroui/react';
import {
    Bot,
    User as UserIcon,
    Wifi,
    WifiOff,
    AlertCircle,
    Loader2,
    Trash2,
    LogIn,
    SendHorizonal,
    Clock,
    Link as LinkIcon,
} from 'lucide-react';
import { ConnectionStatus } from '@src/domain/chat/value_objects/connection-status.value-object';
import { useChat } from '@src/presentation/contexts/ChatContext';
import { useAuth } from '@src/presentation/contexts/AuthContext';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobile } from '@src/shared/hooks/useMobile';
import { PascoDetailModal } from '@src/presentation/components/PascoDetailModal';
import { Linkify } from '@src/shared/utils/linkify';
import { useWhatsAppLink } from '@src/presentation/contexts/WhatsAppLinkContext';

const EXAMPLE_QUERIES = [
    "Show me Calculus PASCOs for Semester 1 2023/2024",
    "I want pascos which has questions on integration by parts",
    "STAT 448",
    "Find PSYC 222 motivation and emotion documents for Semester 2",
    "I need Linear Algebra pascos from academic year 2022/2023",
];

export default function ChatInterfaceRevamped() {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const isMobile = useMobile();
    const { handleWhatsAppAction, needsLinking } = useWhatsAppLink();
    const {
        messages,
        connectionStatus,
        isConnected,
        isSending,
        sendMessage,
        connect,
        clearMessages,
        selectedMessageId,
        selectMessage
    } = useChat();

    const [inputValue, setInputValue] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const { isOpen, onOpen, onClose } = useDisclosure();

    // Modal state for mobile pasco viewing
    const [selectedPascoId, setSelectedPascoId] = useState<string | null>(null);
    const [isPascoModalOpen, setIsPascoModalOpen] = useState(false);

    // Handle pasco click - open modal on mobile, navigate on desktop
    const handlePascoClick = (pascoId: string) => {
        if (isMobile) {
            setSelectedPascoId(pascoId);
            setIsPascoModalOpen(true);
        } else {
            navigate(`/pascos/${pascoId}`);
        }
    };

    // Handle modal close
    const handlePascoModalClose = () => {
        setIsPascoModalOpen(false);
        setSelectedPascoId(null);
    };

    // Auto-scroll to bottom
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Remove auto-connect - only connect when user explicitly sends a message or clicks example
    // useEffect(() => {
    //     if (isAuthenticated && connectionStatus.isDisconnected) {
    //         connect().catch((error) => {
    //             console.error('Failed to connect to chat:', error);
    //         });
    //     }

    //     return () => {
    //         if (isAuthenticated) {
    //             disconnect();
    //         }
    //     };
    // }, [isAuthenticated]);

    const handleSendMessage = async () => {
        if (!inputValue.trim()) return;

        // Check if user is authenticated before sending
        if (!isAuthenticated) {
            onOpen(); // Show login modal
            return;
        }

        // Reconnect if disconnected
        if (!isConnected) {
            try {
                await connect();
            } catch (error) {
                console.error('Failed to reconnect:', error);
                return;
            }
        }

        if (isSending) return;

        try {
            await sendMessage(inputValue.trim());
            setInputValue('');
        } catch (error) {
            console.error('Failed to send message:', error);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleExampleClick = async (query: string) => {
        // Check if user is authenticated
        if (!isAuthenticated) {
            onOpen(); // Show login modal
            return;
        }

        // Reconnect if disconnected
        if (!isConnected) {
            try {
                await connect();
            } catch (error) {
                console.error('Failed to reconnect:', error);
                return;
            }
        }

        if (isSending) return;

        try {
            await sendMessage(query);
        } catch (error) {
            console.error('Failed to send example message:', error);
        }
    };

    const getStatusConfig = () => {
        // Show disconnected status for unauthenticated users
        if (!isAuthenticated) {
            return {
                color: 'default' as const,
                icon: <WifiOff className="h-4 w-4" />,
                text: 'Not Connected',
            };
        }

        switch (connectionStatus.status) {
            case ConnectionStatus.CONNECTED:
                return {
                    color: 'success' as const,
                    icon: <Wifi className="h-4 w-4" />,
                    text: 'Connected',
                };
            case ConnectionStatus.CONNECTING:
            case ConnectionStatus.RECONNECTING:
                return {
                    color: 'warning' as const,
                    icon: <Loader2 className="h-4 w-4 animate-spin" />,
                    text: connectionStatus.status === ConnectionStatus.RECONNECTING ? 'Reconnecting...' : 'Connecting...',
                };
            case ConnectionStatus.ERROR:
                return {
                    color: 'danger' as const,
                    icon: <AlertCircle className="h-4 w-4" />,
                    text: 'Error',
                };
            default:
                return {
                    color: 'default' as const,
                    icon: <Clock className="h-4 w-4" />,
                    text: 'Idle',
                };
        }
    };

    const statusConfig = getStatusConfig();

    return (
        <>
            {/* Login Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="sm" backdrop='blur' classNames={{

                base: 'rounded border border-border',
            }}>
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            <div className="p-2 rounded-lg bg-primary/10">
                                <LogIn className="h-5 w-5 text-primary" />
                            </div>
                            <span>Login Required</span>
                        </div>
                    </ModalHeader>
                    <ModalBody>
                        <p className="text-sm text-foreground/70">
                            Please login to use Pepper Bot and send messages.
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="light"
                            onPress={onClose}
                            className='rounded'
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            onPress={() => {
                                onClose();
                                navigate('/auth', { state: { from: window.location.pathname } });
                            }}
                            startContent={<LogIn className="h-4 w-4" />}
                            className='rounded'
                        >
                            Login
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Card className="h-[96%] lg:h-full border border-border rounded shadow-none flex flex-col hover:shadow-custom transition-shadow duration-200">
                {/* Header */}
                <CardHeader className="flex flex-row items-center justify-between gap-3 px-4 py-2 border-b border-border">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                            <Bot className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <h3 className="text-sm font-semibold">Pepper Bot</h3>
                            <p className="text-xs text-foreground/50">Ask anything about PASCOs</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2.5">
                        {isAuthenticated && (
                            <div
                                className={cn(
                                    "flex items-center justify-center w-8 h-8 rounded-full",
                                    "transition-all duration-200 cursor-default",
                                    statusConfig.color === 'success' && "bg-success/10 text-success hover:bg-success/15",
                                    statusConfig.color === 'warning' && "bg-warning/10 text-warning hover:bg-warning/15",
                                    statusConfig.color === 'danger' && "bg-danger/10 text-danger hover:bg-danger/15",
                                    statusConfig.color === 'default' && "bg-default-100 text-default-500 hover:bg-default-200"
                                )}
                                title={statusConfig.text}
                                aria-label={statusConfig.text}
                                role="status"
                            >
                                {statusConfig.icon}
                            </div>
                        )}
                        <Tooltip
                            content={
                                needsLinking
                                    ? "Link your WhatsApp account"
                                    : "Chat on WhatsApp"
                            }
                            placement="bottom"
                        >
                            <button
                                onClick={handleWhatsAppAction}
                                className="relative flex items-center justify-center w-8 h-8 rounded-full bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/20 hover:border-[#25D366]/40 hover:scale-105 active:scale-95 transition-all duration-200"
                                aria-label={
                                    needsLinking
                                        ? "Link your WhatsApp account"
                                        : "Chat with Pepper Bot on WhatsApp"
                                }
                            >
                                <svg className="h-4 w-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                                </svg>
                                {/* Show link indicator if not linked */}
                                {needsLinking && (
                                    <div className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-3 h-3 rounded-full bg-warning border border-background">
                                        <LinkIcon className="h-2 w-2 text-warning-foreground" />
                                    </div>
                                )}
                            </button>
                        </Tooltip>
                        {messages.length > 0 && isAuthenticated && (
                            <Button
                                size="sm"
                                variant="light"
                                isIconOnly
                                onPress={clearMessages}
                                className="min-w-unit-8 rounded hover:scale-105 active:scale-95 transition-transform duration-200"
                                aria-label="Clear chat history"
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                </CardHeader>

                {/* Messages Area */}
                <CardBody className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
                    {messages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center text-center space-y-4 py-4">
                            <div className="p-3 rounded-full bg-primary/5">
                                <Bot className="h-10 w-10 text-primary/40" />
                            </div>
                            <div className="space-y-1 max-w-md">
                                <h4 className="font-medium text-foreground/80 text-sm">
                                    Start a conversation
                                </h4>
                                <p className="text-xs text-foreground/50">
                                    Ask me about PASCO documents, courses, or exams
                                </p>
                            </div>

                            {/* Example Queries */}
                            <div className="space-y-2 w-full max-w-md">
                                <p className="text-[10px] text-foreground/40 uppercase tracking-wide">
                                    Try these examples
                                </p>
                                <div className="flex flex-col gap-1.5">
                                    {EXAMPLE_QUERIES.map((query, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleExampleClick(query)}
                                            className="px-3 py-2 rounded bg-default-50 hover:bg-default-100 text-left text-xs text-foreground/70 hover:text-foreground transition-all duration-200 border border-transparent hover:border-primary/20"
                                        >
                                            {query}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>
                            {messages.map((message) => {
                                const isSelected = selectedMessageId === message.id;
                                const hasDocuments = message.pascoIds.length > 0;
                                const isClickable = !message.isUserMessage && hasDocuments;

                                return (
                                    <div
                                        key={message.id}
                                        className={`flex gap-3 ${message.isUserMessage ? 'flex-row-reverse' : 'flex-row'
                                            }`}
                                    >
                                        <Avatar
                                            icon={
                                                message.isUserMessage ? (
                                                    <UserIcon className="h-4 w-4" />
                                                ) : (
                                                    <Bot className="h-4 w-4" />
                                                )
                                            }
                                            size="sm"
                                            color={message.isUserMessage ? 'primary' : 'secondary'}
                                            className="flex-shrink-0 mt-1"
                                        />
                                        <div
                                            className={`flex flex-col gap-1 max-w-[80%] ${message.isUserMessage ? 'items-end' : 'items-start'
                                                }`}
                                        >
                                            <div
                                                onClick={() => {
                                                    if (isClickable) {
                                                        selectMessage(isSelected ? null : message.id);
                                                    }
                                                }}
                                                className={`px-4 py-2.5 rounded transition-all ${message.isUserMessage
                                                    ? 'bg-primary text-primary-foreground rounded-tr-sm'
                                                    : `bg-default-100 rounded-tl-sm ${isClickable ? 'cursor-pointer hover:bg-default-200' : ''} ${isSelected ? 'ring-2 ring-primary' : ''}`
                                                    }`}
                                            >
                                                <Linkify
                                                    text={message.content}
                                                    className="text-sm whitespace-pre-wrap leading-relaxed"
                                                />
                                            </div>
                                            {!message.isUserMessage && (message.mostRelevantPascos.length > 0 || message.leastRelevantPascos.length > 0) && (
                                                <div className="px-2 py-1 space-y-2">
                                                    {/* Most Relevant */}
                                                    {message.mostRelevantPascos.length > 0 && (
                                                        <div className="space-y-1">
                                                            <p className="text-[10px] text-foreground/60 font-medium">Top Results</p>
                                                            <div className="space-y-1">
                                                                {message.mostRelevantPascos.map((pasco) => (
                                                                    <div
                                                                        key={pasco.pasco_id}
                                                                        onClick={() => handlePascoClick(pasco.pasco_id)}
                                                                        className="flex items-center justify-between p-1.5 bg-primary/5 hover:bg-primary/10 rounded cursor-pointer transition-colors border border-primary/20"
                                                                    >
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="text-xs font-medium line-clamp-1">{pasco.course_code}</p>
                                                                            <p className="text-[10px] text-foreground/60 line-clamp-1">
                                                                                {pasco.course_name}
                                                                            </p>
                                                                            <p className="text-[9px] text-foreground/50">
                                                                                Semester {pasco.semester} • {pasco.academic_year}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* You might also be interested */}
                                                    {message.leastRelevantPascos.length > 0 && (
                                                        <div className="space-y-1">
                                                            <p className="text-[10px] text-foreground/60 font-medium">Related</p>
                                                            <div className="space-y-1">
                                                                {message.leastRelevantPascos.map((pasco) => (
                                                                    <div
                                                                        key={pasco.pasco_id}
                                                                        onClick={() => handlePascoClick(pasco.pasco_id)}
                                                                        className="flex items-center justify-between p-1.5 bg-default-50 hover:bg-default-100 rounded cursor-pointer transition-colors border border-border"
                                                                    >
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="text-xs font-medium line-clamp-1">{pasco.course_code}</p>
                                                                            <p className="text-[10px] text-foreground/60 line-clamp-1">
                                                                                {pasco.course_name}
                                                                            </p>
                                                                            <p className="text-[9px] text-foreground/50">
                                                                                Semester {pasco.semester} • {pasco.academic_year}
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            {hasDocuments && (
                                                <div className={`text-xs px-2 flex items-center gap-1 ${isSelected ? 'text-primary font-medium' : 'text-foreground/50'}`}>
                                                    <span>
                                                        {isSelected ? '📂' : '📄'} {message.pascoIds.length} document{message.pascoIds.length > 1 ? 's' : ''}
                                                    </span>
                                                    {isClickable && (
                                                        <span className="text-[10px]">
                                                            {isSelected ? '(click to hide)' : '(click to view)'}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                            <span className="text-[10px] text-foreground/30 px-2">
                                                {message.timestamp.toLocaleTimeString([], {
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </span>
                                        </div>
                                    </div>
                                );
                            })}
                            {isSending && (
                                <div className="flex gap-3">
                                    <Avatar
                                        icon={<Bot className="h-4 w-4" />}
                                        size="sm"
                                        color="secondary"
                                        className="flex-shrink-0 mt-1"
                                    />
                                    <div className="flex items-center gap-2 px-4 py-2.5 rounded rounded-tl-sm bg-default-100">
                                        <span className="text-sm text-foreground/60">Thinking </span>
                                        <div className="flex items-center gap-1">
                                            <div className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                            <div className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                            <div className="w-1 h-1 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    <div ref={messagesEndRef} />
                </CardBody>

                <Divider />

                {/* Input Area */}
                <div className="p-4">
                    <div className="flex gap-2">
                        <Input
                            placeholder={!isAuthenticated ? "Login to send messages..." : "Type your message..."}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={isSending}
                            classNames={{
                                input: "text-sm",
                                inputWrapper: "bg-default-50 rounded-full border border-border focus-within:border-primary/50",
                            }}
                            size="md"
                        />
                        <Button
                            color="primary"
                            isIconOnly
                            onPress={handleSendMessage}
                            isDisabled={!inputValue.trim() || (isAuthenticated && isSending)}
                            // isLoading={isSending}
                            size="md"
                            className="min-w-10 rounded-full"
                        >
                            <SendHorizonal className="h-5 w-5" />
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Pasco Detail Modal for Mobile */}
            <PascoDetailModal
                isOpen={isPascoModalOpen}
                onClose={handlePascoModalClose}
                pascoId={selectedPascoId}
            />
        </>
    );
}
